import * as Sentry from '@sentry/vue';
import { IFirestoreListenerData, IFirestoreQueryData, IFirestoreRealtimeData } from '../core/types';

import { BaseFirestorePlugin } from '../core/BaseFirestorePlugin';

interface SentryFirestorePluginOptions {
  enabled?: boolean;
  includeAllQueries?: boolean; // Set to false to only track slow queries
  slowQueryThresholdMs?: number;
  addBreadcrumbs?: boolean; // Whether to add breadcrumbs for debugging
}

const DEFAULT_OPTIONS: SentryFirestorePluginOptions = {
  enabled: true,
  includeAllQueries: true,
  slowQueryThresholdMs: 1000, // 1 second
  addBreadcrumbs: true // Enable breadcrumbs by default
};

/**
 * Plugin that creates Sentry spans for Firestore operations
 * Integrates with existing Sentry transactions to ensure Firestore spans
 * appear alongside HTTP requests in the Sentry UI.
 */
export class SentryFirestorePlugin extends BaseFirestorePlugin {
  private options: SentryFirestorePluginOptions;
  private activeListenerSpans: Map<string, Sentry.Span> = new Map();

  constructor(options: SentryFirestorePluginOptions = DEFAULT_OPTIONS) {
    super('sentry-firestore-plugin', 'Sentry Firestore Plugin', options.enabled);
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  afterQuery(queryData: IFirestoreQueryData): void {
    try {
      
      // Always add breadcrumb, even for queries we don't track as spans
      if (this.options.addBreadcrumbs) {
        const breadcrumbData: Record<string, any> = {
          collection: queryData.collection,
          operation: `${queryData.queryType}.${queryData.type}`,
          duration_ms: queryData.duration,
          result_count: queryData.resultCount
        };

        if (queryData.queryDetails) {
          if (queryData.queryDetails.filterDetails) {
            breadcrumbData.filters = queryData.queryDetails.filterDetails;
          }
          if (queryData.queryDetails.orderBy) {
            breadcrumbData.order_by = queryData.queryDetails.orderBy;
          }
          if (queryData.queryDetails.limit) {
            breadcrumbData.limit = queryData.queryDetails.limit;
          }
        }

        // Add query error to breadcrumb if exists
        if (queryData.error) {
          breadcrumbData.error = queryData.error;
        }

        Sentry.addBreadcrumb({
          category: 'firestore',
          message: `Firestore ${queryData.queryType} ${queryData.type} on ${queryData.collection}`,
          data: breadcrumbData,
          level: queryData.error ? 'error' : 'info'
        });
      }

      // Skip span creation if needed (for fast queries)
      if (!this.options.includeAllQueries &&
          (!queryData.duration || queryData.duration <= (this.options.slowQueryThresholdMs || 0))) {
            //  console.log('SentryFirestorePlugin: Skipping span creation for collection:', queryData.collection)
        return;
      }

      Sentry.startSpan({
        op: 'db.firestore.query',
        name: `${queryData.queryType}.${queryData.type} on ${queryData.collection}`,
        startTime: queryData.timestamp / 1000 // Convert to seconds for Sentry
      }, (span) => {
        // console.log('SentryFirestorePlugin: Creating span for collection:', queryData.collection, "operation:", 
        //   `${queryData.queryType}.${queryData.type}`, 'timestamp:', queryData.timestamp, "span:", span);

        // Add query details to span
        if (queryData.queryDetails) {
          const { filterDetails, orderBy, limit } = queryData.queryDetails;
  
          span?.setAttribute('collection', queryData.collection);
          span?.setAttribute('query_type', queryData.queryType);
          span?.setAttribute('operation', queryData.type);
  
          if (filterDetails) span?.setAttribute('filters', filterDetails);
          if (orderBy) span?.setAttribute('order_by', orderBy);
          if (limit) span?.setAttribute('limit', limit);
        }
  
        // Add result data
        span?.setAttribute('duration_ms', queryData.duration);
        span?.setAttribute('result_count', queryData.resultCount);
  
        if (queryData.error) {
          span?.setAttribute('error', queryData.error);
          span?.setStatus('error');
        } else {
          span?.setStatus('ok');
        }
  
        // console.log('SentryFirestorePlugin: After Query: Ended span for collection', queryData.collection, "span:", span);
      })
    } catch (error) {
      console.warn('SentryFirestorePlugin: Error in afterQuery', error);
    }
  }

  afterListenerAdded(listenerData: IFirestoreListenerData): void {
    try {
      // Add breadcrumb for listener added
      if (this.options.addBreadcrumbs) {
        const breadcrumbData: Record<string, any> = {
          collection: listenerData.collection,
          listener_id: listenerData.id
        };

        if (listenerData.queryDetails) {
          if (listenerData.queryDetails.filters) {
            breadcrumbData.filters = listenerData.queryDetails.filters;
          }
          if (listenerData.queryDetails.orderBy) {
            breadcrumbData.order_by = listenerData.queryDetails.orderBy;
          }
          if (listenerData.queryDetails.limit) {
            breadcrumbData.limit = listenerData.queryDetails.limit;
          }
        }

        Sentry.addBreadcrumb({
          category: 'firestore.listener',
          message: `Firestore listener added on ${listenerData.collection}`,
          data: breadcrumbData,
          level: 'info'
        });
      }

      Sentry.startSpan({
        op: 'db.firestore.listener.add',
        name: `Listener added on ${listenerData.collection}`,
        startTime: listenerData.timestamp / 1000 // Convert to seconds for Sentry
      }, (span) => {
        // console.log('SentryFirestorePlugin: Creating span for collection:', listenerData.collection, "operation:", `Listener added on ${listenerData.collection}`,
        //             'timestamp:', listenerData.timestamp, "span:", span);
        // Add listener details
        span?.setAttribute('collection', listenerData.collection);
        span?.setAttribute('listener_id', listenerData.id);

        if (listenerData.queryDetails) {
          if (listenerData.queryDetails.filters) {
            span?.setAttribute('filters', listenerData.queryDetails.filters);
          }
          if (listenerData.queryDetails.orderBy) {
            span?.setAttribute('order_by', listenerData.queryDetails.orderBy);
          }
          if (listenerData.queryDetails.limit) {
            span?.setAttribute('limit', listenerData.queryDetails.limit);
          }
        }

        // Store in map for potential updates
        if (span) {
          this.activeListenerSpans.set(listenerData.id, span);
        }

        // console.log('SentryFirestorePlugin: After Listener Added: Ended span for collection', listenerData.collection, "span:", span);
      });
    } catch (error) {
      console.warn('SentryFirestorePlugin: Error in afterListenerAdded', error);
    }
  }

  afterListenerRemoved(listenerData: IFirestoreListenerData): void {
    try {
      // Add breadcrumb for listener removed
      if (this.options.addBreadcrumbs) {
        Sentry.addBreadcrumb({
          category: 'firestore.listener',
          message: `Firestore listener removed from ${listenerData.collection}`,
          data: {
            collection: listenerData.collection,
            listener_id: listenerData.id
          },
          level: 'info'
        });
      }

      Sentry.startSpan({
        op: 'db.firestore.listener.remove',
        name: `Listener removed from ${listenerData.collection}`,
        startTime: listenerData.timestamp / 1000 // Convert to seconds for Sentry
      }, (span) => {
          // console.log('SentryFirestorePlugin: Creating span for collection:', listenerData.collection, "operation:", `Listener removed from ${listenerData.collection}`,
          //           'timestamp:', listenerData.timestamp, "span:", span);
          // Add listener details
          span?.setAttribute('collection', listenerData.collection);
          span?.setAttribute('listener_id', listenerData.id);

          // Remove from tracking
          this.activeListenerSpans.delete(listenerData.id);
          // console.log('SentryFirestorePlugin: After Listener Removed: Ended span for collection', listenerData.collection, "span:", span);
      });
    } catch (error) {
      console.warn('SentryFirestorePlugin: Error in afterListenerRemoved', error);
    }
  }

  afterRealtimeDataReceived(realtimeData: IFirestoreRealtimeData): void {
    try {
      Sentry.startSpan({
        op: 'db.firestore.realtime.update',
        name: `Realtime update on ${realtimeData.collection}`,
        startTime: realtimeData.timestamp / 1000 // Convert to seconds for Sentry
      }, (span) => {
        // Add breadcrumb for realtime update
        if (this.options.addBreadcrumbs) {
          const breadcrumbData: Record<string, any> = {
            collection: realtimeData.collection,
            duration_ms: realtimeData.duration,
            result_count: realtimeData.resultCount
          };

          if (realtimeData.id) {
            breadcrumbData.listener_id = realtimeData.id;
          }

          if (realtimeData.queryDetails) {
            if (realtimeData.queryDetails.filters) {
              breadcrumbData.filters = realtimeData.queryDetails.filters;
            }
            if (realtimeData.queryDetails.orderBy) {
              breadcrumbData.order_by = realtimeData.queryDetails.orderBy;
            }
            if (realtimeData.queryDetails.limit) {
              breadcrumbData.limit = realtimeData.queryDetails.limit;
            }
          }

          Sentry.addBreadcrumb({
            category: 'firestore.realtime',
            message: `Firestore realtime update on ${realtimeData.collection}`,
            data: breadcrumbData,
            level: 'info'
          });
        }

        // console.log('SentryFirestorePlugin: Creating span for collection:', realtimeData.collection, "operation:", `Realtime update on ${realtimeData.collection}`,
        //             'timestamp:', realtimeData.timestamp, "span:", span);
        // Add update details
        span?.setAttribute('collection', realtimeData.collection);
        span?.setAttribute('duration_ms', realtimeData.duration);
        span?.setAttribute('result_count', realtimeData.resultCount);

        if (realtimeData.id) {
          span?.setAttribute('listener_id', realtimeData.id);
        }

        if (realtimeData.queryDetails) {
          if (realtimeData.queryDetails.filters) {
            span?.setAttribute('filters', realtimeData.queryDetails.filters);
          }
          if (realtimeData.queryDetails.orderBy) {
            span?.setAttribute('order_by', realtimeData.queryDetails.orderBy);
          }
          if (realtimeData.queryDetails.limit) {
            span?.setAttribute('limit', realtimeData.queryDetails.limit);
          }
        }

        // console.log('SentryFirestorePlugin: After Realtime Data Received: Ended span for collection', realtimeData.collection, "span:", span);
      });
    } catch (error) {
      console.warn('SentryFirestorePlugin: Error in afterRealtimeDataReceived', error);
    }
  }
}
